import { useEffect, useState } from 'react';
import { Check, ImagePlus, Sparkles, X } from 'lucide-react';
import { mockStandardizePhoto, standardizePhotoAI } from '../../utils/photoStatus';
import { useQueue } from '../../context/QueueContext';

// Comparação ORIGINAL × PADRONIZADA × ESTILIZADA (IA) após o envio da foto real.
// Padronizada: a IA de visão apenas LOCALIZA o tecido e o recorte é determinístico
// (pixels 100% fiéis). Estilizada: imagem REGENERADA pela IA (drapeado editorial) —
// pode deslocar levemente o tom; a fidelidade de cor/estampa/textura é conferida
// visualmente pelo admin antes de aprovar. Nada é salvo sem aprovação.
export default function PhotoReviewModal({ original, variantName, productName = '', onChoose, onRetake, onClose, onReject = null, precomputed = null, queueInfo = null }) {
  const { user } = useQueue();
  const [std, setStd] = useState(precomputed);
  const [styled, setStyled] = useState(null);
  const [styling, setStyling] = useState(false);
  const [styleError, setStyleError] = useState('');

  useEffect(() => {
    setStyled(null);
    setStyleError('');
    if (precomputed) {
      setStd(precomputed);
      return undefined;
    }
    let live = true;
    setStd(null);
    (async () => {
      try {
        const r = await standardizePhotoAI(original, user?.role);
        if (live) setStd(r);
      } catch {
        try {
          const local = await mockStandardizePhoto(original);
          if (live) setStd({ ...local, ai: false, cropApplied: false });
        } catch {
          if (live) setStd(null);
        }
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [original, precomputed]);

  // Sob demanda (cada geração consome 1 imagem do provedor): a foto real da variante
  // é a referência visual; a IA reapresenta o tecido drapeado em estilo editorial.
  const generateStyled = async () => {
    setStyling(true);
    setStyleError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/stylize-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Role': user?.role || '' },
        body: JSON.stringify({ photo: original, productName, variantName }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || `IA ${res.status}`);
      setStyled({ standard: d.styled, thumb: d.thumb });
    } catch (e) {
      setStyleError(e.message || 'Não foi possível gerar a versão estilizada agora.');
    } finally {
      setStyling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-testid="photo-review-modal">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-serif text-lg text-navy">
              Revisar foto — {variantName}
              {queueInfo && (
                <span className="ml-2 rounded-full bg-navy-light px-2.5 py-0.5 align-middle text-[11px] font-semibold text-navy" data-testid="photo-review-queue-info">
                  foto {queueInfo.index} de {queueInfo.total}
                </span>
              )}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="photo-review-mode-note">
              {std && !std.ai
                ? 'Padronização: IA indisponível — foto inteira centralizada (sem recorte inteligente). Cor, estampa e textura nunca são alteradas.'
                : 'Padronizada: recorte determinístico (pixels fiéis). Estilizada: imagem regenerada pela IA — confira se a cor/textura ficaram fiéis antes de aprovar.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar" data-testid="photo-review-close" className="rounded-full p-2 text-navy hover:bg-navy-light">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className={`mt-4 grid grid-cols-2 gap-3 ${styled || styling ? 'sm:grid-cols-3' : ''}`}>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Original</p>
            <img src={original} alt="Foto original" className="mt-1 aspect-square w-full rounded-xl object-cover" data-testid="photo-review-original" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Padronizada</p>
            {std ? (
              <div className="relative">
                <img src={std.standard} alt="Foto padronizada" className="mt-1 aspect-square w-full rounded-xl object-cover" data-testid="photo-review-standard" />
                {std.cropApplied && (
                  <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-md bg-navy/85 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white" data-testid="photo-review-crop-badge">
                    <Sparkles className="h-3 w-3" /> Recorte IA
                  </span>
                )}
              </div>
            ) : (
              <div className="mt-1 flex aspect-square w-full items-center justify-center rounded-xl bg-navy-light text-xs text-muted-foreground" data-testid="photo-review-loading">
                Padronizando…
              </div>
            )}
          </div>
          {(styled || styling) && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Estilizada (IA)</p>
              {styled ? (
                <img src={styled.standard} alt="Foto estilizada pela IA" className="mt-1 aspect-square w-full rounded-xl object-cover" data-testid="photo-review-styled" />
              ) : (
                <div className="mt-1 flex aspect-square w-full items-center justify-center rounded-xl bg-navy-light text-xs text-muted-foreground" data-testid="photo-styled-loading">
                  Gerando versão estilizada…
                </div>
              )}
            </div>
          )}
        </div>

        {!styled && (
          <button
            type="button"
            onClick={generateStyled}
            disabled={styling}
            data-testid="photo-generate-styled"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-navy/30 px-4 py-3 text-xs font-bold text-navy hover:bg-navy-light disabled:opacity-40"
          >
            <Sparkles className="h-4 w-4" />
            {styling ? 'GERANDO VERSÃO ESTILIZADA…' : 'GERAR VERSÃO ESTILIZADA COM IA'}
          </button>
        )}
        {!styled && (
          <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
            Reapresenta o tecido drapeado, em estilo editorial. Consome 1 geração de imagem; a aprovação continua manual.
          </p>
        )}
        {styleError && (
          <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-red-600" role="alert" data-testid="photo-styled-error">
            {styleError}
          </p>
        )}

        <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => std && onChoose('standard', std)}
            disabled={!std}
            data-testid="photo-use-standard"
            className="flex items-center justify-center gap-2 rounded-full bg-navy px-4 py-3.5 text-xs font-bold text-white hover:bg-navy-hover disabled:opacity-40"
          >
            <Check className="h-4 w-4" /> USAR PADRONIZADA
          </button>
          {styled && (
            <button
              type="button"
              onClick={() => onChoose('styled', styled)}
              data-testid="photo-use-styled"
              className="flex items-center justify-center gap-2 rounded-full bg-navy-dark px-4 py-3.5 text-xs font-bold text-white hover:bg-navy"
            >
              <Sparkles className="h-4 w-4" /> USAR ESTILIZADA (IA)
            </button>
          )}
          <button
            type="button"
            onClick={() => std && onChoose('original', std)}
            disabled={!std}
            data-testid="photo-use-original"
            className="flex items-center justify-center gap-2 rounded-full border border-navy/25 px-4 py-3.5 text-xs font-bold text-navy hover:bg-navy-light disabled:opacity-40"
          >
            <Check className="h-4 w-4" /> USAR ORIGINAL
          </button>
          <button
            type="button"
            onClick={onRetake}
            data-testid="photo-retake"
            className="flex items-center justify-center gap-2 rounded-full border border-amber-400/60 px-4 py-3.5 text-xs font-bold text-amber-700 hover:bg-amber-50"
          >
            <ImagePlus className="h-4 w-4" /> ENVIAR OUTRA FOTO
          </button>
          {onReject && (
            <button
              type="button"
              onClick={onReject}
              data-testid="photo-reject"
              className="flex items-center justify-center gap-2 rounded-full border border-red-300 px-4 py-3.5 text-xs font-bold text-red-700 hover:bg-red-50 sm:col-span-2"
            >
              <X className="h-4 w-4" /> RECUSAR FOTO ENVIADA
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
